import type {
  ChartPoint,
  Metric,
  TableColumn,
  ToolResultChart,
  ToolResultMetrics,
  ToolResultNote,
  ToolResultTable,
  ToolResultView,
} from './agent-tool-result.model';
import {
  type ValueFormat,
  asArray,
  asObject,
  asString,
  firstPresent,
  formatChange,
  formatDateLabel,
  formatValue,
  humanizeKey,
  isIdKey,
  toNumber,
  valueAt,
} from './agent-tool-result-format';

interface FieldSpec {
  key: string;
  label: string;
  // Tried in order; the later paths read the flat shape Agents in production still sends.
  paths: string[];
  format: ValueFormat;
}

interface TableOptions {
  currency?: string;
  total?: number;
  note?: string;
}

interface QueryResult {
  columns: string[];
  rows: unknown[][];
  // Empty without a comparison.
  changes: unknown[][];
  isMetric: boolean[];
  formats: ValueFormat[];
  currency?: string;
  partialPeriods: Set<string>;
  truncated: boolean;
  todayNote?: string;
  coverageNote?: ToolResultNote;
}

const MAX_TABLE_ROWS = 50;
const PARTIAL_MARK = ' *';
const PARTIAL_NOTE = 'agent.result.partial-note';
const INCLUDES_TODAY_NOTE = 'agent.result.includes-today-note';
const COVERAGE_NOTE = 'agent.result.coverage-note';
const CHANGE_COLUMN_LABEL = 'agent.result.change-column';
const PERIOD_COLUMN = 'period';
const ROW_METRICS_PREFIX = 'metrics.';
const CAMPAIGN_PREFIX = 'campaign.';
const CAMPAIGN_ROW_FIELDS: ReadonlySet<string> = new Set(['layouts', 'widgetCount', 'shareOfAssistedSales']);

const CAMPAIGN_CARDS: FieldSpec[] = [
  field('impressions', 'agent.result.impressions', ['engagement.impressions', 'impressions'], 'number'),
  field('clickThroughs', 'agent.result.clicks', ['engagement.clickThroughs', 'clicks'], 'number'),
  field('ctr', 'agent.result.ctr', ['engagement.ctr'], 'percent'),
  field('formResponses', 'agent.result.conversions', ['engagement.formResponses', 'conversions'], 'number'),
  field('formResponseRate', 'agent.result.form-response-rate', ['engagement.formResponseRate'], 'percent'),
  field('assistedSales', 'agent.result.assisted-sales', ['assistedSales.lastTouchRevenue', 'revenue'], 'money'),
];

const OVERVIEW_CARDS: FieldSpec[] = [
  field('sessions', 'agent.result.sessions', ['traffic.sessions'], 'number'),
  field('revenue', 'agent.result.revenue', ['commerce.revenue'], 'money'),
  field('purchases', 'agent.result.purchases', ['commerce.purchases'], 'number'),
  field('impressions', 'agent.result.impressions', ['engagement.impressions'], 'number'),
  field('formResponses', 'agent.result.conversions', ['engagement.formResponses'], 'number'),
  field('assistedSales', 'agent.result.assisted-sales', ['assistedSales.lastTouchRevenue'], 'money'),
  field('shareOfSiteRevenue', 'agent.result.share-of-site-revenue', ['assistedSales.shareOfSiteRevenue'], 'percent'),
];

const CAMPAIGN_LIST_COLUMNS: FieldSpec[] = [
  field('name', 'agent.result.name', ['name'], 'text'),
  field('status', 'agent.result.status', ['servingState', 'status'], 'state'),
  field('type', 'agent.result.type', ['type'], 'state'),
];

const WIDGET_COLUMNS: FieldSpec[] = [
  field('widget', 'agent.result.widget', ['widgetName'], 'text'),
  field('status', 'agent.result.status', ['status'], 'state'),
  field('impressions', 'agent.result.impressions', ['metrics.engagement.impressions'], 'number'),
  field('clickThroughs', 'agent.result.clicks', ['metrics.engagement.clickThroughs'], 'number'),
  field('formResponses', 'agent.result.conversions', ['metrics.engagement.formResponses'], 'number'),
  field('formResponseRate', 'agent.result.form-response-rate', ['metrics.engagement.formResponseRate'], 'percent'),
  field('assistedSales', 'agent.result.assisted-sales', ['metrics.assistedSales.lastTouchRevenue'], 'money'),
];

const TOP_CAMPAIGN_COLUMNS: FieldSpec[] = [
  field('campaign', 'agent.result.campaign', ['campaign.name'], 'text'),
  field('status', 'agent.result.status', ['campaign.servingState', 'campaign.status'], 'state'),
  field('assistedSales', 'agent.result.assisted-sales', ['metrics.assistedSales.lastTouchRevenue'], 'money'),
  field(
    'purchasingSessions',
    'agent.result.purchasing-sessions',
    ['metrics.assistedSales.lastTouchPurchasingSessions'],
    'number',
  ),
  field('shareOfAssistedSales', 'agent.result.share-of-assisted-sales', ['shareOfAssistedSales'], 'percent'),
  field('impressions', 'agent.result.impressions', ['metrics.engagement.impressions'], 'number'),
  field('formResponses', 'agent.result.conversions', ['metrics.engagement.formResponses'], 'number'),
];

// The first non-zero series wins, so an engagement-only campaign doesn't chart a flat revenue line.
const TREND_SERIES: FieldSpec[] = [
  field('assistedSales', 'agent.result.assisted-sales', ['assistedSales.lastTouchRevenue', 'revenue'], 'money'),
  field('formResponses', 'agent.result.conversions', ['engagement.formResponses', 'conversions'], 'number'),
  field('clickThroughs', 'agent.result.clicks', ['engagement.clickThroughs', 'clicks'], 'number'),
  field('impressions', 'agent.result.impressions', ['engagement.impressions', 'impressions'], 'number'),
];

export const CURATED_BUILDERS: Record<string, (content: unknown) => ToolResultView | undefined> = {
  campaigns_list: campaignList,
  site_overview: siteOverview,
  onsite_campaigns_summary: campaignSummary,
  onsite_campaigns_widget_breakdown: widgetBreakdown,
  onsite_campaigns_trend: campaignTrend,
  top_campaigns: topCampaigns,
  campaign_query: queryResult,
  commerce_query: queryResult,
  feedback_query: queryResult,
  signup_query: queryResult,
  traffic_query: queryResult,
};

function field(key: string, label: string, paths: string[], format: ValueFormat): FieldSpec {
  return { key, label, paths, format };
}

function campaignList(content: unknown): ToolResultTable | undefined {
  const list = asObject(content);
  const rows = Array.isArray(content) ? asArray(content) : asArray(list?.['campaigns']);
  const total = list?.['totalMatching'];
  return table(rows, CAMPAIGN_LIST_COLUMNS, { total: typeof total === 'number' ? total : undefined });
}

function siteOverview(content: unknown): ToolResultMetrics | undefined {
  const report = asObject(content);
  const live = valueAt(report, 'campaigns.live');
  const extra: Metric[] =
    typeof live === 'number' ? [{ label: 'agent.result.live-campaigns', value: formatValue(live, 'number') }] : [];
  return cards(asObject(report?.['current']), OVERVIEW_CARDS, report, extra);
}

function campaignSummary(content: unknown): ToolResultMetrics | undefined {
  const report = asObject(content);
  return cards(asObject(report?.['metrics']), CAMPAIGN_CARDS, report);
}

function widgetBreakdown(content: unknown): ToolResultTable | undefined {
  const report = asObject(content);
  return table(asArray(report?.['widgets']), WIDGET_COLUMNS, {
    currency: asString(report?.['currency']),
    note: includesTodayNote(report),
  });
}

function topCampaigns(content: unknown): ToolResultTable | undefined {
  const report = asObject(content);
  const matching = report?.['matchingCampaigns'];
  const campaigns = Array.isArray(report?.['columns']) ? campaignsFromTable(report) : asArray(report?.['campaigns']);
  return table(campaigns, TOP_CAMPAIGN_COLUMNS, {
    currency: asString(report?.['currency']),
    total: typeof matching === 'number' ? matching : undefined,
    note: includesTodayNote(report),
  });
}

function campaignsFromTable(report: Record<string, unknown> | undefined): Record<string, unknown>[] {
  const columns = columnsOf(report?.['columns']);
  const comparison = asObject(report?.['comparison']);
  const changeColumns = columnsOf(comparison?.['columns']);
  const changeRows = rowsOf(comparison?.['changes']);
  return rowsOf(report?.['rows']).map((cells, rowIndex) => {
    const changes: Record<string, unknown> = {};
    changeColumns.forEach((name, index) => {
      const change = changeRows[rowIndex]?.[index];
      if (Array.isArray(change)) {
        changes[name] = { absolute: change[0], relative: change[1] };
      }
    });
    const row: Record<string, unknown> = { changes };
    columns.forEach((name, index) => setAt(row, campaignPath(name), cells[index]));
    return row;
  });
}

// The table flattens the nested campaign row: identity fields, the row's own fields, then dotted metric paths.
function campaignPath(column: string): string {
  if (column.includes('.')) {
    return ROW_METRICS_PREFIX + column;
  }
  return CAMPAIGN_ROW_FIELDS.has(column) ? column : CAMPAIGN_PREFIX + column;
}

function setAt(target: Record<string, unknown>, path: string, value: unknown): void {
  const parts = path.split('.');
  let container = target;
  for (const part of parts.slice(0, -1)) {
    const next = asObject(container[part]) ?? {};
    container[part] = next;
    container = next;
  }
  container[parts[parts.length - 1]] = value;
}

function campaignTrend(content: unknown): ToolResultChart | undefined {
  const buckets = asArray(asObject(content)?.['buckets']);
  if (buckets.length < 2) {
    return undefined;
  }
  const series = buckets.map((bucket) => asObject(bucket['metrics']));
  const valueOf = (metrics: Record<string, unknown> | undefined, spec: FieldSpec): number =>
    toNumber(firstPresent(metrics, spec.paths)?.value);
  const chosen =
    TREND_SERIES.find((spec) => series.some((metrics) => valueOf(metrics, spec) > 0)) ??
    TREND_SERIES[TREND_SERIES.length - 1];
  const points: ChartPoint[] = buckets.map((bucket, index) => {
    const start = typeof bucket['start'] === 'string' ? bucket['start'] : '';
    const mark = bucket['complete'] === false ? PARTIAL_MARK : '';
    return { label: formatDateLabel(start) + mark, value: valueOf(series[index], chosen) };
  });
  return {
    kind: 'chart',
    metricLabel: chosen.label,
    points,
    note: buckets.some((bucket) => bucket['complete'] === false) ? PARTIAL_NOTE : undefined,
  };
}

function queryResult(content: unknown): ToolResultView | undefined {
  const query = parseQuery(content);
  if (!query) {
    return undefined;
  }
  return query.rows.length === 1 && query.isMetric.every(Boolean) ? queryCards(query) : queryTable(query);
}

// Rows are positional under columns, and a comparison's changes line up with them cell for cell.
function parseQuery(content: unknown): QueryResult | undefined {
  const result = asObject(content);
  const columns = columnsOf(result?.['columns']);
  const rows = rowsOf(result?.['rows']);
  if (!result || columns.length === 0 || rows.length === 0) {
    return undefined;
  }
  const units = asObject(result['metrics']);
  const isMetric = columns.map((name, index) =>
    isMetricColumn(
      name,
      rows.map((row) => row[index]),
    ),
  );
  return {
    columns,
    rows,
    changes: rowsOf(asObject(result['comparison'])?.['changes']),
    isMetric,
    formats: columns.map((name, index) => queryFormat(name, isMetric[index], units)),
    currency: asString(result['currency']),
    partialPeriods: new Set(
      asArray(result['partialPeriods'])
        .map((period) => period['period'])
        .filter((period): period is string => typeof period === 'string'),
    ),
    truncated: result['truncated'] === true,
    todayNote: includesTodayNote(result),
    coverageNote: coverageNote(result),
  };
}

function queryCards(query: QueryResult): ToolResultMetrics {
  return {
    kind: 'metrics',
    metrics: query.columns.map((name, index) => ({
      label: humanizeKey(name),
      value: queryCell(query, query.rows[0], index),
      change: formatChange(query.changes[0]?.[index]),
    })),
    note: query.todayNote,
    coverageNote: query.coverageNote,
  };
}

function queryTable(query: QueryResult): ToolResultTable {
  const { columns, rows, changes } = query;
  const hasChange = (index: number): boolean => changes.length > 0 && query.isMetric[index];
  const tableColumns = columns.flatMap((name, index): TableColumn[] => {
    const column = { key: name, label: humanizeKey(name) };
    return hasChange(index) ? [column, changeColumn(name, column.label)] : [column];
  });
  const shown = rows.slice(0, MAX_TABLE_ROWS);
  return {
    kind: 'table',
    columns: tableColumns,
    rows: shown.map((row, rowIndex) => {
      const formatted: Record<string, string> = {};
      columns.forEach((name, index) => {
        formatted[name] = queryCell(query, row, index);
        if (hasChange(index)) {
          formatted[changeKey(name)] = formatChange(changes[rowIndex]?.[index]) ?? '';
        }
      });
      return formatted;
    }),
    total: rows.length,
    truncated: rows.length > shown.length,
    truncatedUpstream: query.truncated,
    note: query.partialPeriods.size > 0 ? PARTIAL_NOTE : query.todayNote,
    coverageNote: query.coverageNote,
  };
}

function queryCell(query: QueryResult, row: unknown[], index: number): string {
  const column = query.columns[index];
  const mark = column === PERIOD_COLUMN && query.partialPeriods.has(String(row[index])) ? PARTIAL_MARK : '';
  return formatValue(row[index], query.formats[index], query.currency, column) + mark;
}

function changeColumn(key: string, metricLabel: string): TableColumn {
  return { key: changeKey(key), label: CHANGE_COLUMN_LABEL, metricLabel };
}

function changeKey(key: string): string {
  return `${key}:change`;
}

function cards(
  root: Record<string, unknown> | undefined,
  specs: FieldSpec[],
  report: Record<string, unknown> | undefined,
  extra: Metric[] = [],
): ToolResultMetrics | undefined {
  if (!root) {
    return undefined;
  }
  const currency = asString(report?.['currency']);
  const changes = asObject(report?.['changes']);
  const metrics: Metric[] = [];
  for (const spec of specs) {
    const found = firstPresent(root, spec.paths);
    if (!found || (typeof found.value !== 'number' && typeof found.value !== 'string')) {
      continue;
    }
    metrics.push({
      label: spec.label,
      value: formatValue(found.value, spec.format, currency, spec.key),
      change: formatChange(changes?.[found.path]),
    });
  }
  metrics.push(...extra);
  return metrics.length > 0 ? { kind: 'metrics', metrics, note: includesTodayNote(report) } : undefined;
}

function table(
  rows: Record<string, unknown>[],
  specs: FieldSpec[],
  options: TableOptions = {},
): ToolResultTable | undefined {
  if (rows.length === 0) {
    return undefined;
  }
  const shown = rows.slice(0, MAX_TABLE_ROWS);
  const total = Math.max(options.total ?? rows.length, rows.length);
  const compared = specs.filter((spec) => shown.some((row) => rowChange(row, spec) !== undefined));
  return {
    kind: 'table',
    columns: specs.flatMap((spec): TableColumn[] => {
      const column: TableColumn =
        spec.format === 'state'
          ? { key: spec.key, label: spec.label, translated: true }
          : { key: spec.key, label: spec.label };
      return compared.includes(spec) ? [column, changeColumn(spec.key, spec.label)] : [column];
    }),
    rows: shown.map((row) => {
      const formatted: Record<string, string> = {};
      for (const spec of specs) {
        formatted[spec.key] = formatValue(
          firstPresent(row, spec.paths)?.value,
          spec.format,
          options.currency,
          spec.key,
        );
        if (compared.includes(spec)) {
          formatted[changeKey(spec.key)] = formatChange(rowChange(row, spec)) ?? '';
        }
      }
      return formatted;
    }),
    total,
    truncated: total > shown.length,
    note: options.note,
  };
}

// A compared row's changes are keyed by path under its own metrics block, like a report's top-level changes.
function rowChange(row: Record<string, unknown>, spec: FieldSpec): unknown {
  const path = spec.paths.find((candidate) => candidate.startsWith(ROW_METRICS_PREFIX));
  return path === undefined ? undefined : asObject(row['changes'])?.[path.slice(ROW_METRICS_PREFIX.length)];
}

// Query results saved before the window block carry the flag at the root.
function includesTodayNote(report: Record<string, unknown> | undefined): string | undefined {
  return firstPresent(report, ['window.includesToday', 'includesToday'])?.value === true
    ? INCLUDES_TODAY_NOTE
    : undefined;
}

// The later start keeps the note true for both windows.
function coverageNote(result: Record<string, unknown>): ToolResultNote | undefined {
  const starts = ['coverage.completeFrom', 'comparison.coverage.completeFrom']
    .map((path) => asString(valueAt(result, path)))
    .filter((start): start is string => start !== undefined);
  if (starts.length === 0) {
    return undefined;
  }
  const latest = starts.reduce((current, start) => (start > current ? start : current));
  return { key: COVERAGE_NOTE, params: { date: formatValue(latest, 'date') } };
}

function columnsOf(value: unknown): string[] {
  return Array.isArray(value) ? value.filter((column): column is string => typeof column === 'string') : [];
}

function rowsOf(value: unknown): unknown[][] {
  return Array.isArray(value) ? value.filter((row): row is unknown[] => Array.isArray(row)) : [];
}

// Facets come back as text, except widget_id, which is an id; every metric cell is a number or null.
// The wire format carries no column role, so a facet column that is null in every row reads as a metric.
function isMetricColumn(name: string, values: unknown[]): boolean {
  return !isIdKey(name) && values.every((value) => value === null || value === undefined || typeof value === 'number');
}

function queryFormat(name: string, isMetric: boolean, units: Record<string, unknown> | undefined): ValueFormat {
  if (!isMetric) {
    return name === PERIOD_COLUMN ? 'date' : 'text';
  }
  const unit = valueAt(units, `${name}.unit`);
  if (unit === 'fraction') {
    return 'percent';
  }
  return unit === 'currency' ? 'money' : 'number';
}
